import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { Grid, Typography } from '@mui/material';
import StructuredModal from '../structuredModal/structuredModal';
import ControlledFileUploadField from '../controlledComponents/ControlledFileUploadField.component';
import ControlledSelectField from '../controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../controlledComponents/ControlledTextField.component';
import {
  TDocument,
  TDocumentType,
  TDocumentStatus,
} from 'src/apis/documents/documents.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import { zOptionalDateStringFromInput } from 'src/utils/zodFormHelpers';

interface AddEditDocumentProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (document: TDocument) => void;
  onUploadProgress: (isUploading: boolean, message: string) => void;
  document: TDocument | null;
  isLoading: boolean;
  errorMessage: string;
}

type DocumentFormData = {
  document: {
    documentName: string;
    title?: string;
    description?: string;
    verificationDate?: string;
    status: TDocumentStatus;
  };
  documentType: TDocumentType;
  remarks?: string;
};

const getDocumentFormSchema = (
  translateText: (key: string) => string
): ZodType<DocumentFormData> =>
  z.object({
    document: z.object({
      documentName: z.string().min(1, translateText('common.fieldRequired')),
      title: z.string().optional(),
      description: z.string().optional(),
      verificationDate: zOptionalDateStringFromInput({
        message: translateText('common.dateInvalid') || undefined,
      }),
      status: z.nativeEnum(TDocumentStatus, {
        required_error: translateText('common.fieldRequired'),
      }),
    }),
    documentType: z.nativeEnum(TDocumentType, {
      required_error: translateText('common.fieldRequired'),
    }),
    remarks: z.string().optional(),
  }) as ZodType<DocumentFormData>;

const getDocumentTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TDocumentType.BIRTH_CERTIFICATE,
      value: translateText('documents.birthCertificate') || 'Birth Certificate',
    },
    {
      key: TDocumentType.TRANSFER_CERTIFICATE,
      value:
        translateText('documents.transferCertificate') || 'Transfer Certificate',
    },
    {
      key: TDocumentType.PREVIOUS_MARKSHEET,
      value: translateText('documents.previousMarksheet') || 'Previous Marksheet',
    },
    {
      key: TDocumentType.MEDICAL_CERTIFICATE,
      value:
        translateText('documents.medicalCertificate') || 'Medical Certificate',
    },
    {
      key: TDocumentType.AADHAR_CARD,
      value: translateText('documents.aadharCard') || 'Aadhar Card',
    },
    {
      key: TDocumentType.PAN_CARD,
      value: translateText('documents.panCard') || 'PAN Card',
    },
    {
      key: TDocumentType.SALARY_SLIP,
      value: translateText('documents.salarySlip') || 'Salary Slip',
    },
    {
      key: TDocumentType.RESIGNATION_LETTER,
      value: translateText('documents.resignationLetter') || 'Resignation Letter',
    },
    {
      key: TDocumentType.JOINING_LETTER,
      value: translateText('documents.joiningLetter') || 'Joining Letter',
    },
    {
      key: TDocumentType.PROMOTION_LETTER,
      value: translateText('documents.promotionLetter') || 'Promotion Letter',
    },
    {
      key: TDocumentType.LEAVE_LETTER,
      value: translateText('documents.leaveLetter') || 'Leave Letter',
    },
    {
      key: TDocumentType.PHOTO,
      value: translateText('documents.photo') || 'Photo',
    },
    {
      key: TDocumentType.OTHER,
      value: translateText('documents.other') || 'Other',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TDocumentStatus.SUBMITTED,
      value: translateText('documents.submitted') || 'Submitted',
    },
    {
      key: TDocumentStatus.PENDING,
      value: translateText('documents.pending') || 'Pending',
    },
    {
      key: TDocumentStatus.VERIFIED,
      value: translateText('documents.verified') || 'Verified',
    },
    {
      key: TDocumentStatus.REJECTED,
      value: translateText('documents.rejected') || 'Rejected',
    },
  ];

const AddEditDocument: FC<AddEditDocumentProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onUploadProgress,
  document,
  isLoading,
  errorMessage,
}) => {
  const { translateText } = useTranslation();
  const documentFormSchema = getDocumentFormSchema(translateText);

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const defaultValues: DocumentFormData = {
    document: {
      documentName: '',
      title: '',
      description: '',
      verificationDate: '',
      status: TDocumentStatus.PENDING,
    },
    documentType: TDocumentType.OTHER,
    remarks: '',
  };

  const methods = useForm<DocumentFormData>({
    resolver: zodResolver(documentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (document && isOpen) {
      reset({
        document: {
          documentName: document.documentName || '',
          title: document.documentName || '',
          description: document.description || '',
          verificationDate: document.verificationDate || '',
          status: document.status || TDocumentStatus.PENDING,
        },
        documentType: document.documentType || TDocumentType.OTHER,
        remarks: document.remarks || '',
      });
    } else if (isOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, isOpen, reset]);

  const onSubmitForm = (data: DocumentFormData) => {
    const submissionDate = new Date().toISOString().split('T')[0];
    const timestamp = Date.now();

    const documentData: TDocument = {
      documentId: document?.documentId || '',
      userId: document?.userId || loggedInUserId,
      documentType: data.documentType,
      documentName: data.document.documentName,
      submissionDate: document?.submissionDate || submissionDate,
      verificationDate: data.document.verificationDate || undefined,
      status: data.document.status,
      remarks: data.remarks || undefined,
      fileUrl: document?.fileUrl || undefined,
      thumbnailUrl: document?.thumbnailUrl || undefined,
      description: data.document.description || undefined,
      uploadedAt: document?.uploadedAt || {
        userId: loggedInUserId,
        timestamp,
      },
    };

    onSubmit(documentData);
  };

  return (
    <div data-testid="add-edit-document-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          document
            ? translateText('documents.editDocument') || 'Edit Document'
            : translateText('documents.addDocument') || 'Add Document'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          document
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isLoading}
        footerNegativeAction={onClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          errorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="document-error-message"
            >
              {errorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="document-form"
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              sx={{ p: { xs: 1, sm: 2 } }}
              data-testid="document-form-container"
            >
              <Grid item xs={12} data-testid="document-details-section">
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {translateText('documents.documentDetails') ||
                    'Document Details'}
                </Typography>
              </Grid>

              <Grid item xs={12} data-testid="document-file-upload-field">
                <ControlledFileUploadField
                  control={control}
                  name="document.documentName"
                  id="document-upload"
                  label={translateText('common.selectFile') || 'Select File'}
                  sectionTitle={
                    translateText('documents.documentFile') || 'Document File'
                  }
                  isRequired
                  errorMessage={errors.document?.documentName?.message}
                  titleError={errors.document?.title?.message}
                  descriptionError={errors.document?.description?.message}
                  verificationError={errors.document?.verificationDate?.message}
                  statusError={errors.document?.status?.message}
                  onUploadProgress={onUploadProgress}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-testid="document-type-field">
                <ControlledSelectField
                  control={control}
                  name="documentType"
                  id="document-type"
                  label={
                    translateText('documents.documentType') || 'Document Type'
                  }
                  itemList={getDocumentTypeOptions(translateText)}
                  errorMessage={errors.documentType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-testid="document-status-field">
                <ControlledSelectField
                  control={control}
                  name="document.status"
                  id="document-status"
                  label={translateText('documents.status') || 'Status'}
                  itemList={getStatusOptions(translateText)}
                  errorMessage={errors.document?.status?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="document-remarks-field">
                <ControlledTextField
                  control={control}
                  name="remarks"
                  id="document-remarks"
                  label={translateText('common.remarks') || 'Remarks'}
                  errorMessage={errors.remarks?.message}
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditDocument;
